/**
 * The PDF for a handover protocol an admin recorded on the tenant's behalf.
 *
 * Why this is a separate builder from `protocol-pdf.ts`: that one renders a
 * TENANT WALKTHROUGH — room photos, the inventory table, defects, a signature.
 * An admin recording has none of those. It has whatever paperwork the admin
 * holds (a scanned paper protocol, phone photos) plus their written statement,
 * and the honest document is an attestation page followed by that evidence.
 *
 * The attestation lives in the PDF rather than only in the database because
 * this file is what everyone downstream actually reads: the card's "Open PDF",
 * the optional tenant email, and — the one that matters — the move-out bundle,
 * where `copyPages` merges it in front of the lessor's representative deciding
 * whether to keep someone's deposit. A database flag never reaches them.
 *
 * pdf-lib throughout rather than jsPDF: the same library has to draw the page
 * AND absorb the uploaded PDFs, and mixing two PDF stacks to produce one file
 * is how page-size and font bugs get in.
 */
import { PDFDocument, StandardFonts, rgb, type PDFFont } from "pdf-lib";
import { sanitize } from "@/lib/invoice-stamp";
import { coliviqMarkBytes, coliviqMarkMetrics } from "@/lib/brand-mark";
import type { ProtocolKind } from "@/lib/protocol";

/**
 * Uploads this builder can absorb.
 *
 * Matches `documentUploadError` in the admin Documents tab, and the omission of
 * HEIC is deliberate rather than an oversight: pdf-lib can only embed JPEG and
 * PNG, so a HEIC photo of a paper protocol would have to be silently dropped —
 * and a protocol that quietly omits the evidence the admin believes is in it is
 * worse than one that refuses the file and says why.
 */
export const RECORD_ACCEPTED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
] as const;

export const RECORD_ACCEPT_ATTR = RECORD_ACCEPTED_TYPES.join(",");

/** 20 MB, matching MAX_UPLOAD_BYTES in the admin Documents tab. */
export const RECORD_MAX_FILE_BYTES = 20 * 1024 * 1024;

export type RecordFileKind = "pdf" | "image";

export type ClassifiedFiles = {
  pdfs: File[];
  images: File[];
  /** Files that cannot go in, with the reason to show the admin. */
  rejected: Array<{ file: File; reason: string }>;
};

function fileKind(file: File): RecordFileKind | null {
  const type = (file.type || "").toLowerCase();
  if (type === "application/pdf" || /\.pdf$/i.test(file.name)) return "pdf";
  if (
    type === "image/jpeg" ||
    type === "image/png" ||
    /\.(jpe?g|png)$/i.test(file.name)
  )
    return "image";
  return null;
}

/**
 * Split the picked files into what the builder does with them.
 *
 * Pure and exported so the dialog can show the admin exactly what will happen
 * BEFORE they commit, and so the branch logic is unit-tested without a canvas.
 */
export function classifyRecordFiles(files: File[]): ClassifiedFiles {
  const out: ClassifiedFiles = { pdfs: [], images: [], rejected: [] };
  for (const file of files) {
    if (file.size > RECORD_MAX_FILE_BYTES) {
      out.rejected.push({ file, reason: "larger than 20 MB" });
      continue;
    }
    const kind = fileKind(file);
    if (kind === "pdf") out.pdfs.push(file);
    else if (kind === "image") out.images.push(file);
    else out.rejected.push({ file, reason: "not a PDF, JPG or PNG" });
  }
  return out;
}

/** One-line description of what saving will produce, for the dialog. */
export function describeRecordOutcome(files: ClassifiedFiles): string {
  const { pdfs, images } = files;
  const parts: string[] = [];
  if (pdfs.length)
    parts.push(`${pdfs.length} uploaded PDF${pdfs.length === 1 ? "" : "s"}`);
  if (images.length)
    parts.push(`${images.length} photo${images.length === 1 ? "" : "s"}`);
  if (!parts.length)
    return "No documents attached — the protocol will record your note only.";
  return `The protocol will be filed as an attestation page followed by ${parts.join(" and ")}.`;
}

export type RecordedProtocolPdfInput = {
  ref: string;
  kind: ProtocolKind;
  /** The date of the walkthrough being recorded — often backdated. */
  walkthroughDate: string | null;
  /** When the admin filed this record. */
  recordedAt: string;
  recordedByName: string;
  note: string;
  booking: {
    property_name: string | null;
    room_number: string | null;
    address: string | null;
  } | null;
  tenant: { name: string | null; email: string | null };
  files: File[];
};

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/**
 * The attestation itself, as lines of text.
 *
 * Pure and exported so the wording — the part with legal weight — is asserted
 * in a test rather than eyeballed in a rendered PDF. Bilingual because the
 * reader is a German lessor's representative and the admin surface is English.
 */
export function buildAttestationLines(
  input: Pick<
    RecordedProtocolPdfInput,
    "kind" | "recordedByName" | "recordedAt" | "walkthroughDate"
  >
): string[] {
  const kindDe =
    input.kind === "move_in" ? "Einzugsprotokoll" : "Auszugsprotokoll";
  const kindEn = input.kind === "move_in" ? "move-in" : "move-out";
  return [
    `Dieses ${kindDe} wurde am ${formatDate(input.recordedAt)} von ${input.recordedByName} ` +
      `(coliviq) im Namen der Mieterin / des Mieters erfasst.`,
    `Es handelt sich NICHT um eine von der Mieterin / dem Mieter in der App ` +
      `durchgeführte und unterzeichnete Begehung. Eine Unterschrift der Mieterin / ` +
      `des Mieters liegt nicht vor.`,
    "",
    `This ${kindEn} protocol was recorded on ${formatDate(input.recordedAt)} by ` +
      `${input.recordedByName} (coliviq) on the tenant's behalf.`,
    `It is NOT a walkthrough completed and signed by the tenant in the app, and ` +
      `carries no tenant signature.`,
  ];
}

const A4: [number, number] = [595.28, 841.89];
const MARGIN = 48;
const INK = rgb(0.06, 0.09, 0.16);
const MUTED = rgb(0.42, 0.45, 0.5);
const RULE = rgb(0.85, 0.87, 0.9);

/** Greedy wrap against the real measured width, not a character count. */
function wrap(
  text: string,
  font: PDFFont,
  size: number,
  maxWidth: number
): string[] {
  if (!text.trim()) return [""];
  const out: string[] = [];
  for (const paragraph of text.split(/\r?\n/)) {
    if (!paragraph.trim()) {
      out.push("");
      continue;
    }
    let line = "";
    for (const word of paragraph.split(/\s+/)) {
      const candidate = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
        line = candidate;
      } else {
        if (line) out.push(line);
        line = word;
      }
    }
    if (line) out.push(line);
  }
  return out;
}

/** Height of the header wordmark. */
const LOGO_HEIGHT = 14;

/**
 * The header logo: the coliviq wordmark, alone.
 *
 * The mark is the logo PNG itself (brand-mark.ts), cropped to its ink, so `yTop`
 * — the top of the mark, matching how the surrounding `draw` helper thinks about
 * a line — converts to pdf-lib's bottom-left anchor by subtracting the height.
 * The embed is passed in rather than made here: pdf-lib's `embedPng` is async
 * and this runs inside the synchronous layout pass.
 */
function drawColiviqLogo(
  page: ReturnType<PDFDocument["addPage"]>,
  mark: Awaited<ReturnType<PDFDocument["embedPng"]>>,
  x: number,
  yTop: number
): void {
  const box = coliviqMarkMetrics(LOGO_HEIGHT);
  page.drawImage(mark, {
    x,
    y: yTop - box.height,
    width: box.width,
    height: box.height,
  });
}

/**
 * Attestation page, then every uploaded file, in the order the admin picked.
 *
 * Uploaded PDFs are copied page-for-page and never re-rendered — a scanned,
 * hand-signed paper protocol has to reach the representative exactly as it was
 * scanned.
 *
 * A file pdf-lib cannot parse (encrypted, truncated) THROWS, naming the file.
 * The alternative — skipping it — would hand someone a deposit decision built
 * on evidence the admin believes they attached and did not.
 */
export async function buildRecordedProtocolPdf(
  input: RecordedProtocolPdfInput
): Promise<Blob> {
  const { pdfs, images, rejected } = classifyRecordFiles(input.files);
  if (rejected.length) {
    throw new Error(
      `Cannot attach ${rejected.map((r) => `"${r.file.name}" (${r.reason})`).join(", ")}.`
    );
  }

  const doc = await PDFDocument.create();
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const body = await doc.embedFont(StandardFonts.Helvetica);

  const page = doc.addPage(A4);
  const width = A4[0] - MARGIN * 2;
  let y = A4[1] - MARGIN;

  const draw = (
    text: string,
    font: PDFFont,
    size: number,
    color = INK,
    gap = 4
  ) => {
    for (const line of wrap(sanitize(text), font, size, width)) {
      y -= size + gap;
      if (line) page.drawText(line, { x: MARGIN, y, size, font, color });
    }
  };

  drawColiviqLogo(page, await doc.embedPng(coliviqMarkBytes()), MARGIN, y);
  // The logo occupies the band the wordmark used to, so the lines under it keep
  // their spacing: advance `y` by the same amount `draw` would have.
  y -= LOGO_HEIGHT + 4;
  draw(
    input.kind === "move_in"
      ? "Einzugsprotokoll · Move-in protocol"
      : "Auszugsprotokoll · Move-out protocol",
    body,
    11,
    MUTED
  );
  draw("Vom Betreiber erfasst · Recorded by the operator", bold, 11, MUTED, 8);

  y -= 10;
  page.drawLine({
    start: { x: MARGIN, y },
    end: { x: A4[0] - MARGIN, y },
    thickness: 1,
    color: RULE,
  });
  y -= 8;

  const facts: Array<[string, string]> = [
    ["Referenz / Reference", input.ref],
    ["Objekt / Property", input.booking?.property_name ?? "—"],
    ["Zimmer / Room", input.booking?.room_number ?? "—"],
    ["Adresse / Address", input.booking?.address ?? "—"],
    ["Mieter:in / Tenant", input.tenant.name || input.tenant.email || "—"],
    ["Begehung / Walkthrough date", formatDate(input.walkthroughDate)],
    ["Erfasst am / Recorded on", formatDate(input.recordedAt)],
    ["Erfasst von / Recorded by", input.recordedByName],
  ];
  for (const [label, value] of facts) {
    y -= 15;
    page.drawText(sanitize(label), {
      x: MARGIN,
      y,
      size: 9.5,
      font: bold,
      color: MUTED,
    });
    page.drawText(sanitize(value), {
      x: MARGIN + 165,
      y,
      size: 9.5,
      font: body,
      color: INK,
    });
  }

  y -= 22;
  draw("Hinweis · Notice", bold, 12);
  y -= 2;
  for (const line of buildAttestationLines(input)) {
    draw(line, body, 10, INK, 3);
  }

  y -= 18;
  draw("Anmerkung des Betreibers · Operator note", bold, 12);
  y -= 2;
  draw(input.note || "—", body, 10, INK, 3);

  if (pdfs.length || images.length) {
    y -= 18;
    draw("Anlagen · Attachments", bold, 12);
    y -= 2;
    for (const file of [...pdfs, ...images]) {
      draw(`• ${file.name}`, body, 10, INK, 3);
    }
  }

  // Uploaded PDFs first, in pick order, then the photos.
  for (const file of pdfs) {
    let src: PDFDocument;
    try {
      src = await PDFDocument.load(await file.arrayBuffer());
    } catch {
      throw new Error(
        `"${file.name}" could not be read as a PDF (it may be encrypted or damaged). ` +
          `Remove it or replace it with a readable copy — the protocol was not saved.`
      );
    }
    const copied = await doc.copyPages(src, src.getPageIndices());
    for (const p of copied) doc.addPage(p);
  }

  for (const file of images) {
    const bytes = await file.arrayBuffer();
    const isPng =
      file.type.toLowerCase() === "image/png" || /\.png$/i.test(file.name);
    let embedded;
    try {
      embedded = isPng ? await doc.embedPng(bytes) : await doc.embedJpg(bytes);
    } catch {
      throw new Error(
        `"${file.name}" could not be read as an image. Remove it or re-export it — ` +
          `the protocol was not saved.`
      );
    }
    const imgPage = doc.addPage(A4);
    const boxW = A4[0] - MARGIN * 2;
    const boxH = A4[1] - MARGIN * 2 - 22;
    const scale = Math.min(boxW / embedded.width, boxH / embedded.height, 1);
    const w = embedded.width * scale;
    const h = embedded.height * scale;
    imgPage.drawImage(embedded, {
      x: (A4[0] - w) / 2,
      y: A4[1] - MARGIN - h,
      width: w,
      height: h,
    });
    imgPage.drawText(sanitize(file.name), {
      x: MARGIN,
      y: MARGIN,
      size: 9,
      font: body,
      color: MUTED,
    });
  }

  const bytes = await doc.save();
  return new Blob([bytes as BlobPart], { type: "application/pdf" });
}
